/**
 * Navigation Banner Component
 * Inline navigation dropdowns for use within the unified header bar
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Calendar, CalendarDays, Globe, BookOpen, Download, Star, Sparkles, GraduationCap, Clock, MoonStar } from 'lucide-react';
import MeditationIcon from '../icons/MeditationIcon';
import { cn } from '../../lib/utils';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export type ViewType =
  | 'daily-panchanga'
  | 'monthly-panchanga'
  | 'calendar-download'
  | 'mahabharata-dating'
  | 'ramayana-dating'
  | 'buddha-dating'
  | 'planetary-positions'
  | 'planetary-alignment'
  | 'full-chart'
  | 'muhurtha'
  | 'birth-chart'
  | 'daily-best-times'
  | 'learnings'
  | 'sky-simulator'
  | 'dhyana'
  | 'privacy'
  | 'terms'
  | 'support'
  | 'landing';

interface NavigationBannerProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  selectedLocale?: string;
  onLocaleChange?: (locale: string) => void;
}

interface NavSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: {
    id: ViewType;
    label: string;
    icon: React.ReactNode;
    isWIP?: boolean;
    isExperimental?: boolean;
  }[];
}

export const NavigationBanner: React.FC<NavigationBannerProps> = ({
  currentView,
  onViewChange,
  selectedLocale,
  onLocaleChange,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const navRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const rowRef = useRef<HTMLElement>(null);
  const focusFirstItem = useRef(false);
  const [fades, setFades] = useState({ left: false, right: false });
  const compact = useMediaQuery('(max-width: 1279px)');  // the two-row header (below xl)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setExpandedSections(new Set());
    }, 250);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setExpandedSections(new Set());
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const sections: NavSection[] = [
    {
      id: 'panchanga',
      label: 'Panchanga',
      icon: <Calendar className="h-4 w-4" />,
      items: [
        {
          id: 'daily-panchanga',
          label: 'Daily Panchanga',
          icon: <Calendar className="h-4 w-4" />,
        },
        {
          id: 'monthly-panchanga',
          label: 'Monthly Panchanga',
          icon: <CalendarDays className="h-4 w-4" />,
        },
        {
          id: 'calendar-download',
          label: 'Download Calendar',
          icon: <Download className="h-4 w-4" />,
        },
      ],
    },
    {
      id: 'dhyana',
      label: 'Dhyāna',
      icon: <MeditationIcon className="h-4 w-4" />,
      items: [
        {
          id: 'dhyana',
          label: 'Nāḍī Śodhana',
          icon: <MeditationIcon className="h-4 w-4" />,
        },
      ],
    },
    {
      id: 'akasha-darshana',
      label: 'Ākāśa Darśana',
      icon: <MoonStar className="h-4 w-4" />,
      items: [
        {
          id: 'planetary-alignment',
          label: 'Kāla Chakra',
          icon: <Globe className="h-4 w-4" />,
        },
        {
          id: 'planetary-positions',
          label: 'Graha Sthāna',
          icon: <Globe className="h-4 w-4" />,
        },
        {
          id: 'sky-simulator',
          label: 'Sky Simulator',
          icon: <MoonStar className="h-4 w-4" />,
        },
      ],
    },
    {
      id: 'jyotish',
      label: 'Jyotish',
      icon: <Sparkles className="h-4 w-4" />,
      items: [
        {
          id: 'birth-chart',
          label: 'Janma Graha Mudr\u0101',
          icon: <Sparkles className="h-4 w-4" />,
          isExperimental: true,
        },
        {
          id: 'muhurtha',
          label: '\u015aubha K\u0101la',
          icon: <Star className="h-4 w-4" />,
          isExperimental: true,
        },
        {
          id: 'daily-best-times',
          label: 'Divasasya \u015are\u1e63\u1e6dha',
          icon: <Clock className="h-4 w-4" />,
          isExperimental: true,
        },
      ],
    },
    {
      id: 'historical',
      label: 'Historical Datings',
      icon: <BookOpen className="h-4 w-4" />,
      items: [
        {
          id: 'mahabharata-dating',
          label: 'Mahabharata Dating',
          icon: <BookOpen className="h-4 w-4" />,
          isWIP: true,
        },
        {
          id: 'ramayana-dating',
          label: 'Ramayana Dating',
          icon: <BookOpen className="h-4 w-4" />,
          isWIP: true,
        },
        {
          id: 'buddha-dating',
          label: 'Buddha Dating',
          icon: <BookOpen className="h-4 w-4" />,
          isWIP: true,
        },
      ],
    },
    {
      id: 'jnana-kosa',
      label: 'Jñāna Kośa',
      icon: <GraduationCap className="h-4 w-4" />,
      items: [
        {
          id: 'learnings',
          label: 'Jijñāsā (Did You Know?)',
          icon: <GraduationCap className="h-4 w-4" />,
        },
      ],
      // Future items (not yet routable, shown in the Learnings page itself):
      // - Paddhati (How to Read)
      // - Śabda Kośa (Glossary)
    },
  ];

  const toggleSection = (sectionId: string, focusFirst = false) => {
    const newExpanded = new Set<string>();
    // If clicking on an already expanded section, close it. Otherwise, open only this one.
    if (!expandedSections.has(sectionId)) {
      newExpanded.add(sectionId);
      focusFirstItem.current = focusFirst;
    }
    setExpandedSections(newExpanded);
  };

  const closeMenu = (returnFocusTo?: string) => {
    setExpandedSections(new Set());
    if (returnFocusTo) buttonRefs.current[returnFocusTo]?.focus();
  };

  const handleItemClick = (itemId: ViewType, _sectionId: string) => {
    onViewChange(itemId);
    setExpandedSections(new Set());  // Close dropdown after selection
  };

  const openSection = sections.find((s) => expandedSections.has(s.id) && s.items.length > 1);

  // The menu lives outside the sideways-scrolling row (which would clip it),
  // so place it under its section here and keep its right edge on screen (B-153).
  // Opened from the keyboard, focus moves into it, since it follows the whole row in DOM order.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const button = openSection && buttonRefs.current[openSection.id];
    const cell = navRef.current;
    if (!menu || !button || !cell) return;
    const cellLeft = cell.getBoundingClientRect().left;
    const want = button.getBoundingClientRect().left - cellLeft;
    const maxLeft = window.innerWidth - 8 - menu.offsetWidth - cellLeft;
    menu.style.left = `${Math.max(8 - cellLeft, Math.min(want, maxLeft))}px`;
    if (focusFirstItem.current) {
      menu.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
      focusFirstItem.current = false;
    }
  }, [openSection]);

  // A fade on whichever edge of the row has more sections past it: a row that
  // happens to end on a whole word otherwise looks complete (B-153).
  const updateFades = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const left = row.scrollLeft > 1;
    const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 1;
    setFades((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, []);
  useEffect(() => {
    updateFades();
    if (typeof ResizeObserver === 'undefined' || !rowRef.current) return;
    const ro = new ResizeObserver(updateFades);
    ro.observe(rowRef.current);
    return () => ro.disconnect();
  }, [updateFades]);
  const fadeMask = fades.left || fades.right
    ? `linear-gradient(to right, ${fades.left ? 'transparent, #000 28px' : '#000'}, ${fades.right ? '#000 calc(100% - 28px), transparent' : '#000'})`
    : undefined;

  const menuKeys = (e: React.KeyboardEvent, sectionId: string) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      items[e.key === 'Home' ? 0 : items.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      closeMenu(sectionId);
    }
  };

  return (
    // `contents`: the header grid in App places the sections and the controls
    // directly, one row when wide, two rows below xl (STYLE_GUIDE "Header and navigation").
    <div className="contents">
      <div
        ref={navRef}
        className="relative col-span-2 row-start-2 min-w-0 -mx-4 xl:col-span-1 xl:col-start-2 xl:row-start-1 xl:mx-0"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onBlur={(e) => {
          // Close when focus leaves the sections and their menu (e.g. Tab to the language picker).
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setExpandedSections(new Set());
        }}
      >
      <nav
        ref={rowRef}
        aria-label="Main"
        data-row-fade={fades.right ? 'right' : fades.left ? 'left' : 'none'}
        onScroll={updateFades}
        // A finger or wheel moving the row closes its menu; scrolls caused by
        // keyboard focus do not, or a menu could never be reached by Tab.
        onTouchMove={() => setExpandedSections(new Set())}
        onWheel={() => setExpandedSections(new Set())}
        style={fadeMask ? { WebkitMaskImage: fadeMask, maskImage: fadeMask } : undefined}
        className="flex items-center gap-1 overflow-x-auto whitespace-nowrap px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden xl:flex-wrap xl:justify-center xl:overflow-visible xl:whitespace-normal xl:px-0"
      >
      {sections.map((section) => {
        const isExpanded = expandedSections.has(section.id);
        const single = section.items.length === 1 ? section.items[0] : null;
        // full-chart is the legacy alias of Kāla Chakra (KALA_CHAKRA_MERGE_SPEC).
        const here = currentView === 'full-chart' ? 'planetary-alignment' : currentView;
        const isCurrent = section.items.some((i) => i.id === here);
        const buttonClass = cn(
          "flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 min-h-11 xl:min-h-0 rounded-md text-sm xl:text-[15px] font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          // The page you are on: accent text and an underline bar, distinct from hover's fill.
          isCurrent && "text-brand dark:text-brand-dark shadow-[inset_0_-2px_0_currentColor] hover:text-brand dark:hover:text-brand-dark",
        );
        const onFocus = (e: React.FocusEvent<HTMLElement>) =>
          e.currentTarget.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
        if (single) {
          // One page: a link, not a one-item menu.
          return (
            <button
              key={section.id}
              data-nav-section={section.id}
              onClick={() => handleItemClick(single.id, section.id)}
              onFocus={onFocus}
              aria-current={isCurrent ? 'page' : undefined}
              className={buttonClass}
            >
              {section.icon}
              <span>{section.label}</span>
            </button>
          );
        }
        return (
            <button
              key={section.id}
              ref={(el) => { buttonRefs.current[section.id] = el; }}
              data-nav-section={section.id}
              data-current={isCurrent || undefined}
              onClick={() => toggleSection(section.id)}
              onFocus={onFocus}
              aria-haspopup="true"
              aria-expanded={isExpanded}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  if (isExpanded && e.key === 'ArrowDown') {
                    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
                  } else {
                    toggleSection(section.id, true);
                  }
                } else if (e.key === 'Escape' && isExpanded) {
                  setExpandedSections(new Set());
                }
              }}
              className={cn(buttonClass, isExpanded && "bg-accent text-accent-foreground")}
            >
              {section.icon}
              <span>{section.label}</span>
              {isExpanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
        );
      })}
      </nav>

            {openSection && (
              <div ref={menuRef} role="menu" aria-label={openSection.label} onKeyDown={(e) => menuKeys(e, openSection.id)} className="absolute top-full mt-1 bg-popover border-2 border-primary/20 rounded-md shadow-xl z-50 min-w-[200px] backdrop-blur-sm bg-background/95">
                {openSection.items.map((item) => {
                  const isActive = (currentView === 'full-chart' ? 'planetary-alignment' : currentView) === item.id;
                  return (
                    <button
                      key={item.id}
                      role="menuitem"
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleItemClick(item.id, openSection.id)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2 min-h-11 xl:min-h-0 text-sm text-left transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        "first:rounded-t-md last:rounded-b-md",
                        isActive && "text-brand dark:text-brand-dark font-medium"
                      )}
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {item.isWIP && (
                        <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
                          WIP
                        </span>
                      )}
                      {item.isExperimental && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                          Experimental
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
      </div>

      {/* Language Selector and Theme Toggle */}
      <div className="col-start-2 row-start-1 justify-self-end flex items-stretch gap-0 shrink-0 rounded-lg border border-input bg-background shadow-sm xl:col-start-3">
        {/* Language Selector */}
        {selectedLocale && onLocaleChange && (
          <select
            id="locale-select"
            aria-label="Language"
            className="h-11 min-h-[44px] box-border rounded-l-lg border-0 bg-transparent pl-3 pr-1 py-0 m-0 text-sm font-medium [outline:none] [&:focus]:outline-none [&:focus]:ring-0 cursor-pointer"
            value={selectedLocale}
            onChange={(e) => onLocaleChange(e.target.value)}
          >
            {LOCALES.map(([code, own, english]) => (
              <option key={code} value={code}>
                {compact || !english ? own : `${own} (${english})`}
              </option>
            ))}
          </select>
        )}
        <div className="w-px bg-border self-stretch" />
        <ThemeToggle />
      </div>
    </div>
  );
};

// Each language in its own script; the English gloss is dropped in the two-row
// header so its first row fits 360 px (a language picker's usual convention).
const LOCALES: [string, string, string | null][] = [
  ['en', 'English', null],
  ['sa', 'संस्कृतम्', 'Sanskrit'],
  ['hi', 'हिन्दी', 'Hindi'],
  ['te', 'తెలుగు', 'Telugu'],
  ['kn', 'ಕನ್ನಡ', 'Kannada'],
];


export default NavigationBanner;
